import custOfetch from '~/composables/useCustomFetch'
import type { Event, EventStatus } from '~/types/prisma'

export interface CreateEventPayload {
  name: string
  startDate: Date
  endDate: Date
  status: EventStatus
}

export interface UpdateEventPayload extends Partial<CreateEventPayload> {}

export interface EventFeatures {
  id: string | null
  teamChanges: boolean
  scoreboard: boolean
  raffle: boolean
  auction: boolean
  registration: boolean
}

export const useEvent = () => {
  const config = useRuntimeConfig()

  const getAll = async (
  ) => {
    return await custOfetch(`${config.public.apiBase}/event`, {
      method: 'GET',
      credentials: 'include',
    }, false) as Event[]
  }

  const getAllMy = async () => {
    return await custOfetch(`${config.public.apiBase}/event/my`, {
      method: 'GET',
      credentials: 'include',
    }, false) as Event[]
  }

  const getPrimary = async () => {
    return await custOfetch(`${config.public.apiBase}/event/primary`, {
      method: 'GET',
      credentials: 'include',
    }, false) as { eventId: string | null }
  }

  // Flags of the event in context, or of the primary event when there is none.
  const getFeatures = async (primaryEvent = false) => {
    const { eventId } = useContext()
    return await custOfetch(`${config.public.apiBase}/event-features`, {
      method: 'GET',
      credentials: 'include',
      headers: (!primaryEvent && eventId.value) ? { 'x-event-id': eventId.value } : {},
    }, false) as EventFeatures
  }

  const create = async (
    payload: CreateEventPayload,
  ) => {
    return await custOfetch(`${config.public.apiBase}/event`, {
      method: 'POST',
      credentials: 'include',
      body: payload,
    }, false) as Event
  }

  const update = async (
    id: string,
    payload: UpdateEventPayload,
  ) => {
    return await custOfetch(`${config.public.apiBase}/event/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      body: payload,
    }, false) as Event
  }

  const deleteEvent = async (
    id: string,
  ) => {
    return await custOfetch(`${config.public.apiBase}/event/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    }, false) as Event
  }

  const getById = async (id: string) => {
    return await custOfetch(`${config.public.apiBase}/event/${id}`, {
      method: 'GET',
      credentials: 'include',
    }, false) as Event | null
  }

  const inviteToEvent = async (eventId: string, payload: { email: string; eventRoleId: string }) => {
    return await custOfetch(`${config.public.apiBase}/event/${eventId}/invite`, {
      method: 'POST',
      credentials: 'include',
      body: payload,
    }, false)
  }

  const bulkInviteToEvent = async (eventId: string, payload: { emails: string[]; eventRoleId: string; sendEmails?: boolean }) => {
    return await custOfetch(`${config.public.apiBase}/event/${eventId}/invite/bulk`, {
      method: 'POST',
      credentials: 'include',
      body: payload,
    }, false) as { invited: number; failed: string[]; emailsSent: boolean }
  }

  const addDonorInvite = async (eventId: string, payload: { email: string; firstName: string; lastName: string }) => {
    return await custOfetch(`${config.public.apiBase}/event/${eventId}/donor-invite`, {
      method: 'POST',
      credentials: 'include',
      body: payload,
    }, false)
  }

  const preloadDonorsFromPreviousEvent = async (eventId: string) => {
    return await custOfetch(`${config.public.apiBase}/event/${eventId}/donor-invite/preload`, {
      method: 'POST',
      credentials: 'include',
    }, false) as { imported: number; skipped: number }
  }

  const sendAllPendingDonors = async (eventId: string) => {
    return await custOfetch(`${config.public.apiBase}/event/${eventId}/donor-invite/send-all`, {
      method: 'POST',
      credentials: 'include',
    }, false) as { sent: number; failed: number }
  }

  const importJudgesFromEvent = async (eventId: string, payload: { sourceEventId: string }) => {
    return await custOfetch(`${config.public.apiBase}/event/${eventId}/judges/import`, {
      method: 'POST',
      credentials: 'include',
      body: payload,
    }, false) as { imported: number; skipped: number }
  }

  return {
    getAll,
    getAllMy,
    getPrimary,
    getFeatures,
    getById,
    create,
    update,
    deleteEvent,
    inviteToEvent,
    bulkInviteToEvent,
    addDonorInvite,
    preloadDonorsFromPreviousEvent,
    sendAllPendingDonors,
    importJudgesFromEvent,
  }
}
